import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    DealDocument,
    DropdownOptionInterface,
    Snackbar,
} from '@models/models';
import { ConfirmModalComponent } from '@shared/confirm-modal/confirm-modal.component';
import { DocumentService } from '@services/documents.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalModel } from '@models/modal.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@services/snackbar.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ServerDocsService } from '@services/server-docs.service';
import { CRUDService } from '@services/api-calls/crud.service';

@Component({
    selector: 'app-document',
    templateUrl: './document.component.html',
    styleUrls: ['./document.component.scss'],
})
export class DocumentComponent implements OnInit {
    @Input() document: DealDocument;
    @Input() categories: DropdownOptionInterface[];
    @Input() dealId: string;
    @Output() onDelete = new EventEmitter<any>();
    public deleteDocPopup: ModalModel = new ModalModel(false, '', '');
    public editDocPopup: ModalModel = new ModalModel(false);
    public documentStrings: any;
    public snackbar: Snackbar;
    public documentForm: FormGroup;
    public submitted: boolean = false;
    public categoryIndex: number;

    constructor(
        private crudService: CRUDService,
        public dialog: MatDialog,
        private translate: TranslateService,
        private snackbarService: SnackbarService,
        private formBuilder: FormBuilder,
        private docService: ServerDocsService
    ) {}

    ngOnInit(): void {
        this.snackbar = this.snackbarService.snackbarItem;
        this.documentForm = this.formBuilder.group({});
        this.categoryIndex = this.categories.indexOf(
            this.categories.find(
                (cat: DropdownOptionInterface) =>
                    cat.value === this.document.category
            )
        );

        setTimeout(() => {
            this.documentForm.controls[
                'documentName' + this.document.identifier
            ].setValue(this.document.name);
        }, 100);

        this.translate
            .get('FUNNEL.dealPage.documentStrings.successDelete')
            .subscribe((translated: string) => {
                this.documentStrings = {
                    successDelete: translated,
                    errorDelete: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.errorDelete'
                    ),
                    privacityTitle: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.privacityTitle'
                    ),
                    privacityText: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.privacityText'
                    ),
                    privacityYes: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.privacityYes'
                    ),
                    privacityNo: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.privacityNo'
                    ),
                    deleteTitle: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.deltePopupTitle'
                    ),
                    deleteText: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.deltePopupText'
                    ),
                    deleteNo: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.btnDeleteCancel'
                    ),
                    deleteYes: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.btnDeleteAccept'
                    ),
                    editSuccessfuly: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.editSuccessfuly'
                    ),
                    editError: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.editError'
                    ),
                };
            });
    }

    openEditdocumentPopup(): void {
        this.editDocPopup.open();
    }

    managePrivacity(): void {
        if (this.document.internal) {
            const dialogRef = this.dialog.open(ConfirmModalComponent, {
                data: new ModalModel(
                    false,
                    this.documentStrings.privacityTitle,
                    this.documentStrings.privacityText,
                    this.documentStrings.privacityNo,
                    this.documentStrings.privacityYes
                ),
            });
            dialogRef.afterClosed().subscribe((result) => {
                this.document.internal = !result;
                this.updateDocument();
            });
        } else {
            this.document.internal = true;
            this.updateDocument();
        }
    }

    getCategory(event: DropdownOptionInterface) {
        this.document.category = event.value;
    }

    updateDocument(): void {
        const data = {
            name: this.document.name,
            internal: this.document.internal,
            category: this.document.category,
            deal: this.dealId,
        };
        this.crudService
            .run(
                'update',
                'documentation',
                'woct',
                this.document.identifier,
                null,
                data
            )
            .subscribe(
                () => {},
                (error) => {
                    console.log(error);
                }
            );
    }

    downloadFile(documentId: string): void {
        this.docService.getImage(documentId).subscribe(
            (response) => {
                const url = window.URL.createObjectURL(response);
                window.open(url);
            },
            (error) => {
                console.log(error);
                this.snackbar.show(
                    `ERROR ${error.status}:  ${error.statusText}`,
                    'error'
                );
            }
        );
    }

    openDeleteDocumentPopup(document: DealDocument): void {
        this.deleteDocPopup = new ModalModel(
            false,
            this.documentStrings.deleteTitle,
            this.documentStrings.deleteText,
            this.documentStrings.deleteNo,
            this.documentStrings.deleteYes
        );
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: this.deleteDocPopup,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.deleteDocument(document);
            }
        });
    }

    deleteDocument(document: DealDocument): void {
        this.crudService
            .run('delete', 'documentation', 'woct', document.identifier, {
                deal: this.dealId,
            })
            .subscribe(
                () => {
                    this.deleteDocPopup.close();
                    this.snackbar.show(
                        this.documentStrings.successDelete,
                        'success',
                        true,
                        3000
                    );
                    this.onDelete.emit(true);
                },
                (error) => {
                    this.snackbar.show(
                        this.documentStrings.errorDelete,
                        'error',
                        true,
                        3000
                    );
                    console.log(error);
                }
            );
    }

    /**
     * Edit document name and/or category
     */
    editDocument(): void {
        this.submitted = true;
        if (!this.documentForm.invalid) {
            const data = {
                name: this.documentForm.get(
                    'documentName' + this.document.identifier
                ).value,
                category: this.document.category,
                deal: this.dealId,
            };
            this.crudService
                .run(
                    'update',
                    'documentation',
                    'woct',
                    this.document.identifier,
                    null,
                    data
                )
                .subscribe(
                    () => {
                        this.editDocPopup.close();
                        this.snackbar.show(
                            this.documentStrings.editSuccessfuly,
                            'success',
                            true,
                            3000
                        );
                        this.onDelete.emit(true);
                    },
                    (error) => {
                        this.snackbar.show(
                            this.documentStrings.editError,
                            'error',
                            true,
                            3000
                        );
                        console.log(error);
                    }
                );
        }
    }
}
